import React, { useState } from 'react';
import { sectionLabelClass } from '../ChallengeWire/challengeWireFormStyles';
import {
	aiTrainerHeadingClassName,
	aiTrainerMutedTextClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

const CONFIDENCE_STYLES = {
	high: 'bg-green-100 text-green-800 dark:bg-dark-bg dark:text-green-300',
	medium: 'bg-yellow-100 text-yellow-900 dark:bg-dark-bg dark:text-yellow-200',
	low: 'bg-orange-100 text-orange-900 dark:bg-dark-bg dark:text-orange-200'
};

function formatMacro(value, suffix = '') {
	if (value == null || Number.isNaN(Number(value))) return null;

	const rounded = Math.round(Number(value));
	return `${rounded}${suffix}`;
}

function MacroPill({ label, value, unit = '' }) {
	if (value == null) return null;

	return (
		<div className="rounded-xl bg-gray-50 px-3 py-2 text-center dark:bg-dark-bg">
			<p className={`text-xs font-medium uppercase tracking-wide ${aiTrainerMutedTextClassName}`}>{label}</p>
			<p className={`mt-0.5 text-lg font-semibold tabular-nums ${aiTrainerHeadingClassName}`}>
				{value}
				{unit && <span className="text-sm font-medium">{unit}</span>}
			</p>
		</div>
	);
}

function formatFoodMacroLine(macros) {
	if (!macros) return null;

	const parts = [
		formatMacro(macros.calories_kcal) != null ? `${formatMacro(macros.calories_kcal)} cal` : null,
		formatMacro(macros.protein_g) != null ? `${formatMacro(macros.protein_g)}g protein` : null,
		formatMacro(macros.carbs_g) != null ? `${formatMacro(macros.carbs_g)}g carbs` : null,
		formatMacro(macros.fat_g) != null ? `${formatMacro(macros.fat_g)}g fat` : null
	].filter(Boolean);

	return parts.join(' · ');
}

function FoodMacroRow({ macros }) {
	if (!macros) return null;

	const items = [
		formatMacro(macros.calories_kcal) != null ? { label: 'Calories', value: formatMacro(macros.calories_kcal) } : null,
		formatMacro(macros.protein_g) != null ? { label: 'Protein', value: `${formatMacro(macros.protein_g)}g` } : null,
		formatMacro(macros.carbs_g) != null ? { label: 'Carbs', value: `${formatMacro(macros.carbs_g)}g` } : null,
		formatMacro(macros.fat_g) != null ? { label: 'Fat', value: `${formatMacro(macros.fat_g)}g` } : null
	].filter(Boolean);

	if (!items.length) return null;

	return (
		<dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-4">
			{items.map((item) => (
				<div key={item.label}>
					<dt className={`text-[10px] font-medium uppercase tracking-wide ${aiTrainerMutedTextClassName}`}>
						{item.label}
					</dt>
					<dd className={`text-xs font-semibold tabular-nums ${aiTrainerHeadingClassName}`}>{item.value}</dd>
				</div>
			))}
		</dl>
	);
}

function formatMealForCopy(breakdown) {
	const lines = [breakdown.meal_name];
	const totals = breakdown.totals || {};

	if (totals.calories_kcal != null) {
		lines.push(
			`${Math.round(totals.calories_kcal)} cal · ${Math.round(totals.protein_g || 0)}g protein · ${Math.round(
				totals.carbs_g || 0
			)}g carbs · ${Math.round(totals.fat_g || 0)}g fat`
		);
	}

	lines.push('');
	breakdown.foods?.forEach((food) => {
		const macroLine = formatFoodMacroLine(food.macros);
		lines.push(`• ${food.name} — ${food.portion?.label || 'portion unknown'}${macroLine ? ` (${macroLine})` : ''}`);
	});

	if (breakdown.notes) lines.push('', breakdown.notes);
	if (breakdown.disclaimer) lines.push('', breakdown.disclaimer);

	return lines.join('\n').trim();
}

export default function MealBreakdownCard({ breakdown, embedded = false }) {
	const [expanded, setExpanded] = useState(true);
	const [copied, setCopied] = useState(false);

	if (!breakdown?.meal_name || !breakdown?.foods?.length) return null;

	const totals = breakdown.totals || {};
	const confidence = breakdown.confidence || 'medium';

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(formatMealForCopy(breakdown));
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	}

	return (
		<div
			className={
				embedded
					? 'overflow-hidden'
					: 'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-dark-border dark:bg-dark-card'
			}
		>
			<div
				className={
					embedded
						? 'pb-3'
						: 'border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-bg'
				}
			>
				{!embedded && <p className={sectionLabelClass}>Meal breakdown</p>}
				<div className={`${embedded ? '' : 'mt-1 '}flex flex-wrap items-start justify-between gap-2`}>
					<p className={`text-base font-semibold leading-snug ${aiTrainerHeadingClassName}`}>
						{breakdown.meal_name}
					</p>
					<span
						className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
							CONFIDENCE_STYLES[confidence] || CONFIDENCE_STYLES.medium
						}`}
					>
						{confidence} confidence
					</span>
				</div>
			</div>

			<div className={embedded ? 'space-y-4' : 'space-y-4 px-4 py-3'}>
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					<MacroPill label="Calories" value={formatMacro(totals.calories_kcal)} />
					<MacroPill label="Protein" value={formatMacro(totals.protein_g)} unit="g" />
					<MacroPill label="Carbs" value={formatMacro(totals.carbs_g)} unit="g" />
					<MacroPill label="Fat" value={formatMacro(totals.fat_g)} unit="g" />
				</div>

				{totals.fiber_g != null && (
					<p className={`text-xs ${aiTrainerMutedTextClassName}`}>
						Fiber {Math.round(totals.fiber_g)}g
						{totals.sodium_mg != null ? ` · Sodium ~${Math.round(totals.sodium_mg)}mg` : ''}
					</p>
				)}

				<div>
					<p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${aiTrainerMutedTextClassName}`}>
						Foods
					</p>
					<ul className="space-y-2">
						{(expanded ? breakdown.foods : breakdown.foods.slice(0, 2)).map((food, index) => {
							const foodConfidence = food.confidence || 'medium';
							const preparation =
								food.preparation &&
								!food.portion?.label?.toLowerCase().includes(food.preparation.toLowerCase())
									? food.preparation
									: null;

							return (
								<li
									key={`${food.name}-${index}`}
									className="rounded-lg bg-gray-50 px-3 py-2.5 dark:bg-dark-bg"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className={`text-sm font-medium ${aiTrainerHeadingClassName}`}>{food.name}</p>
											<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>
												{food.portion?.label}
												{preparation ? ` · ${preparation}` : ''}
											</p>
										</div>
										{foodConfidence !== 'high' && (
											<span
												className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
													CONFIDENCE_STYLES[foodConfidence] || CONFIDENCE_STYLES.medium
												}`}
											>
												{foodConfidence}
											</span>
										)}
									</div>
									<FoodMacroRow macros={food.macros} />
								</li>
							);
						})}
					</ul>

					{breakdown.foods.length > 2 && (
						<button
							type="button"
							onClick={() => setExpanded((value) => !value)}
							className="mt-2 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
						>
							{expanded ? 'Show less' : `Show all ${breakdown.foods.length} foods`}
						</button>
					)}
				</div>

				{breakdown.notes && (
					<p className={`text-xs leading-relaxed ${aiTrainerMutedTextClassName}`}>{breakdown.notes}</p>
				)}
				{breakdown.disclaimer && (
					<p className={`text-[11px] leading-relaxed ${aiTrainerMutedTextClassName}`}>{breakdown.disclaimer}</p>
				)}
			</div>

			<div
				className={
					embedded
						? 'pt-3'
						: 'border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-bg'
				}
			>
				<button type="button" onClick={handleCopy} className={aiTrainerSecondaryButtonClassName}>
					{copied ? 'Copied!' : 'Copy breakdown'}
				</button>
			</div>
		</div>
	);
}
